// Task List V2 — data + view logic. Single fetch of the enriched feed (keyed on
// the SHARED project-stories key so a drawer edit refreshes grid AND board).
// All filtering/grouping is client-side over that one feed.
//
// MOCK FALLBACK (visual-first), DEMO / PREVIEW OFFICES ONLY (mock-scope.js,
// live e2e F-L7): if the real feed is empty, the hook falls back to the shared
// mockStories fixtures so the demo grid renders rich. A real office shows its
// real (possibly empty) feed. `usingMock` makes the fallback explicit so the UI
// can badge it and tests can assert it. Upstream rows (API or mock) are
// normalized to ONE canonical row shape (see normalizeStory.js) so every
// downstream column/editor is shape-agnostic.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api.js';
import { mockStories } from '@/components/project-view/_mock/sampleData.js';
import { boardStoriesFor } from '@/components/workspace/_mock/board-stories.js';
import { registerMockStories } from '@/components/workspace/_mock/story-registry.js';
import { allowsMockFallback } from '@/components/workspace/_mock/mock-scope.js';
import { normalizeList, topLevel, childrenOf } from './normalizeStory.js';
import { championOfAssignee, championsOfStory } from '@/lib/champion-identity.js';

export const GROUP_BY = ['status', 'priority', 'assignee', 'due_date'];

/**
 * Which group accordion mode leaves expanded by default: the ACTIVE work
 * (`in_progress`) when it has stories, else the first non-empty group in display
 * order (backlog → shaped → in_progress → …), else the first group. Returns the
 * group key, or undefined for no groups.
 */
export function defaultOpenGroupKey(groups) {
  const list = groups ?? [];
  const nonEmpty = list.filter((g) => (g.rows?.length ?? 0) > 0);
  const active = nonEmpty.find((g) => g.key === 'in_progress');
  return (active ?? nonEmpty[0] ?? list[0])?.key;
}
const STATUS_ORDER = [
  'backlog', 'planned', 'in_progress', 'blocked', 'in_review', 'done', 'cancelled',
  // Mission statuses (home Missions view) — funnel order.
  'icebox', 'brainstorming', 'shaping', 'building', 'validating', 'operating', 'shipped', 'archived',
];
const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];
const DUE_DATE_ORDER = ['overdue', 'today', 'this_week', 'this_month', 'later', '__none__'];

/** SHARED feed key — grid and board MUST key identically. */
export function projectStoriesKey(officeId, projectId) {
  return ['office', officeId, 'project-stories', projectId];
}

function dueDateBucket(row) {
  const raw = row.due_date;
  if (!raw) return '__none__';
  const today = new Date().toISOString().slice(0, 10);
  if (raw < today) return 'overdue';
  if (raw === today) return 'today';
  const diff = Math.floor((new Date(raw) - new Date(today)) / 86400000);
  if (diff <= 7) return 'this_week';
  if (diff <= 30) return 'this_month';
  return 'later';
}

function bucketKeyFor(row, groupBy) {
  if (groupBy === 'priority') return row.priority ?? '__none__';
  if (groupBy === 'assignee') {
    // Resolve to the champion (COALESCE(staff_agent_id, id)) : Sven's legacy
    // « Sven (Orchestration) » rows group under « Sven ».
    const first = championOfAssignee((row.assignees ?? [])[0]);
    return first ? (first.name || first.id || '__none__') : '__none__';
  }
  if (groupBy === 'due_date') return dueDateBucket(row);
  return row.status ?? '__none__';
}

function orderFor(groupBy) {
  if (groupBy === 'priority') return PRIORITY_ORDER;
  if (groupBy === 'due_date') return DUE_DATE_ORDER;
  return STATUS_ORDER;
}

/**
 * Stories grouped by champion (« Par champion » view, POC 2026-10-09). A
 * story with several champions appears under each ; unassigned stories close
 * the list. `championKey` (« agent:<champion id> ») narrows to one champion.
 *
 * @param {object[]} rows canonical rows
 * @param {{ championKey?: string|null }} [opts]
 * @returns {Array<{ key: string, label: string, champion: object|null, rows: object[] }>}
 */
export function groupRowsByChampion(rows, { championKey = null } = {}) {
  const groups = new Map();
  const none = [];
  for (const row of rows ?? []) {
    const champions = championsOfStory(row);
    if (champions.length === 0) { none.push(row); continue; } // eslint-disable-line no-continue
    for (const c of champions) {
      if (!groups.has(c.key)) groups.set(c.key, { key: c.key, label: c.name || c.id, champion: c, rows: [] });
      groups.get(c.key).rows.push(row);
    }
  }
  let out = [...groups.values()].sort((a, b) => {
    const ka = a.champion.kind === 'agent' ? 0 : 1;
    const kb = b.champion.kind === 'agent' ? 0 : 1;
    return ka - kb || a.label.localeCompare(b.label);
  });
  if (championKey) return out.filter((g) => g.key === championKey);
  if (none.length) out = [...out, { key: '__none__', label: null, champion: null, rows: none }];
  return out;
}

export function useTaskList({ officeId, projectId, mission, overrideStories, missionIds, draft = false }) {
  // `overrideStories` is the cross-project seam (e.g. the office home's "my
  // stories"): when present, skip the per-project feed entirely and drive every
  // view off the supplied list.
  const feed = useQuery({
    queryKey: projectStoriesKey(officeId, projectId),
    queryFn: () => apiFetch(`/api/offices/${officeId}/projects/${projectId}/stories`),
    enabled: Boolean(officeId && projectId) && !overrideStories,
  });

  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('status');
  const [priorityFilter, setPriorityFilter] = useState([]); // string[]
  const [labelFilter, setLabelFilter] = useState([]); // labelId[]
  const [memberFilter, setMemberFilter] = useState([]); // "kind:id"[]
  const [showFlat, setShowFlat] = useState(false); // flatten subtasks into top level
  const [sortBy, setSortBy] = useState('default'); // 'default' | 'priority' | 'title' | 'due_date'

  // The real feed, if any.
  const apiStories = feed.data?.stories ?? [];
  // Scope the feed: a single `mission` → only its stories ; a `missionIds` set
  // (a group's missions) → that group's stories ; otherwise the whole project.
  const feedStories = mission
    ? apiStories.filter((s) => s.mission_id === mission.id)
    : missionIds
      ? apiStories.filter((s) => missionIds.has(s.mission_id))
      : apiStories;
  const usingMock = !overrideStories && feedStories.length === 0 && allowsMockFallback(officeId);
  // Fallback when there are no real stories: the mission's OWN coherent mock
  // tickets if any, else empty (a reasoning mission / empty group has none) —
  // never the shared generic sample for a scoped view. The full project view
  // falls back to the sample.
  const mockFallback = useMemo(
    () => (mission ? (boardStoriesFor(mission) ?? []) : (missionIds ? [] : mockStories)),
    [mission, missionIds],
  );
  const baseStories = overrideStories ?? (usingMock ? mockFallback : feedStories);
  // A set not yet approved at the shaping gate : every row reads « Brouillon ».
  const sourceStories = useMemo(
    () => (draft ? baseStories.map((s) => ({ ...s, draft: true })) : baseStories),
    [draft, baseStories],
  );

  // Make the rendered mock rows resolvable by the self-fetching StoryDrawer —
  // otherwise clicking a mock story opens a "Story not found" drawer (the id has
  // no server-side record). Only mock rows are registered; real feeds fetch live.
  useEffect(() => {
    if (usingMock) registerMockStories(mockFallback);
  }, [usingMock, mockFallback]);

  // Normalize the FULL list (incl. subtasks) once; derive top-level + children.
  const allRows = useMemo(() => normalizeList(sourceStories), [sourceStories]);
  const rootRows = useMemo(() => topLevel(allRows), [allRows]);

  // Children lookup for inline subtask expansion.
  const childrenOfRow = useMemo(() => {
    const map = new Map();
    for (const r of allRows) {
      if (r.parentId) {
        if (!map.has(r.parentId)) map.set(r.parentId, []);
        map.get(r.parentId).push(r);
      }
    }
    return (id) => map.get(id) ?? [];
  }, [allRows]);

  // Client-side filter over the canonical rows (top-level roots only).
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rootRows.filter((r) => {
      if (term && !(r.title ?? '').toLowerCase().includes(term)) return false;
      if (priorityFilter.length && !priorityFilter.includes(r.priority)) return false;
      if (labelFilter.length && !(r.labels ?? []).some((l) => labelFilter.includes(l.id))) return false;
      if (memberFilter.length) {
        const refs = [
          ...(r.assignees ?? []).map((a) => `${a.kind}:${a.id}`),
          ...championsOfStory(r).map((c) => c.key),
        ];
        if (!refs.some((ref) => memberFilter.includes(ref))) return false;
      }
      return true;
    });
  }, [rootRows, search, priorityFilter, labelFilter, memberFilter]);

  // Apply sort over filtered rows.
  const sorted = useMemo(() => {
    if (sortBy === 'default') return filtered;
    const PRIO_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
    return [...filtered].sort((a, b) => {
      if (sortBy === 'priority') return (PRIO_RANK[a.priority] ?? 9) - (PRIO_RANK[b.priority] ?? 9);
      if (sortBy === 'title') return (a.title ?? '').localeCompare(b.title ?? '');
      if (sortBy === 'due_date') {
        const da = a.due_date ?? '';
        const db2 = b.due_date ?? '';
        if (!da && !db2) return 0;
        if (!da) return 1;
        if (!db2) return -1;
        return da.localeCompare(db2);
      }
      return 0;
    });
  }, [filtered, sortBy]);

  const groups = useMemo(() => {
    const buckets = new Map();
    for (const row of sorted) {
      const key = bucketKeyFor(row, groupBy);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(row);
    }
    const order = orderFor(groupBy);
    const keys = [...buckets.keys()].sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return String(a).localeCompare(String(b));
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return keys.map((key) => ({ key, rows: buckets.get(key) }));
  }, [sorted, groupBy]);

  return {
    feed,
    usingMock,
    // The raw story set actually driving the grid (mission-filtered when in a
    // mission) — sub-views (board/roadmap/graph) reuse it so they filter too.
    sourceStories,
    // Canonical rows (top-level) after filter + sort; full set + children accessor too.
    rows: sorted,
    allRows,
    childrenOf: childrenOfRow,
    groups,
    groupBy,
    setGroupBy,
    search,
    setSearch,
    priorityFilter,
    setPriorityFilter,
    labelFilter,
    setLabelFilter,
    memberFilter,
    setMemberFilter,
    showFlat,
    setShowFlat,
    sortBy,
    setSortBy,
    // Back-compat: a flat list of raw stories (used by the inline add seq calc).
    stories: sourceStories,
  };
}

export { childrenOf };
